import axios from 'axios';

// What may be logged about a failed call: status, code and message — never the error object, which carries the
// request and so the password or token in its body (owner app audit, Low, 4 Nov 2026).
const describeError = (error) => ({
  status: error?.response?.status,
  code: error?.response?.data?.code,
  message: error?.message
});

/**
 * Public API Instance
 *
 * Industry Standard: Separate axios instance for unauthenticated requests
 * Used for: login, register, password reset, public endpoints
 *
 * Benefits:
 * - No accidental token leakage to public endpoints
 * - Clear separation of concerns
 * - Better security (defense in depth)
 * - Easier debugging (know exactly which instance is used)
 */

const OWNER_APP_CONFIG_TIMEOUT_MS = 5000;

// Create axios instance for public/unauthenticated requests
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for logging only (no auth)
publicApi.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('❌ Public API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
publicApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      console.error(`❌ Public API Error ${error.response.status} from ${error.config?.url}`);

      // Standardize error messages for public endpoints
      switch (error.response.status) {
        case 400:
          // Keep original message for validation errors
          break;
        case 423:
          // Account locked - keep original message with unlock time
          break;
        case 403:
          // Account status issues - keep original message
          break;
        case 500:
          // Don't expose internal server errors
          error.response.data = {
            ...error.response.data,
            message: error.response.data.message || 'حدث خطأ في الخادم',
            code: 'SERVER_ERROR'
          };
          break;
        case 404:
          // Endpoint not found
          error.response.data = {
            message: 'الصفحة المطلوبة غير موجودة',
            code: 'NOT_FOUND'
          };
          break;
        default:
          break;
      }
    } else if (error.request) {
      console.error('❌ Public API No Response:', error.message);
      // Network error
      error.message = 'لا يمكن الاتصال بالخادم. تحقق من اتصال الإنترنت';
    }

    return Promise.reject(error);
  }
);

// Public API service methods
const publicApiService = {
  // Authentication endpoints
  login: async (email, password, rememberMe = false) => {
    try {
      const response = await publicApi.post('/api/v1/auth/login', {
        email,
        password,
        rememberMe
      });
      return response.data;
    } catch (error) {
      console.error('Login error:', describeError(error));
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await publicApi.post('/api/v1/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration error:', describeError(error));
      throw error;
    }
  },

  requestPasswordReset: async (email) => {
    try {
      // v3.0: Updated endpoint path to match backend
      const response = await publicApi.post('/api/v1/users/request-password-reset', {
        email
      });
      return response.data;
    } catch (error) {
      console.error('Password reset request error:', describeError(error));
      throw error;
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      // v3.0: Updated endpoint path to match backend
      const response = await publicApi.post('/api/v1/users/reset-password', {
        token,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Password reset error:', describeError(error));
      throw error;
    }
  },

  activateAccount: async (activationToken, password) => {
    try {
      // v3.0: Updated to match backend - requires both token and password
      const response = await publicApi.post('/api/v1/users/activate', {
        activationToken,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Account activation error:', describeError(error));
      throw error;
    }
  },

  // Token refresh (uses refresh token, not access token)
  refreshToken: async (refreshToken) => {
    try {
      const response = await publicApi.post('/api/v1/auth/refresh', {
        refreshToken
      });
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', describeError(error));
      throw error;
    }
  },

  // Server-side end of THIS device's session. Deliberately sent through the public
  // instance: by the time this fires the session is already over locally, and it must not
  // enter the interceptor chain (a 401 here would trigger a refresh cascade). Tokens are
  // passed explicitly because storage has already been cleared. The renewable token in the
  // body names the session even when the 1-hour pass has expired; the pass is the fallback.
  logout: async (token, refreshToken = null) => {
    try {
      const response = await publicApi.post('/api/v1/auth/logout', refreshToken ? { refreshToken } : {}, {
        headers: token ? { 'x-auth-token': token } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Server-side logout error:', describeError(error));
      throw error;
    }
  },

  // The owner store app's settings (tasks/owner-mobile-app-build.md §10 step 5, 8d): `{ status, storeUrl, … }`.
  // The app sends its platform and version in its X-App headers; a phone browser names the platform (the store
  // link for that phone). Short timeout: the check is fail-open, so a slow answer must not hold the owner up.
  getOwnerAppConfig: async ({ platform } = {}) => {
    const response = await publicApi.get('/api/v1/public/owner-app/config', {
      params: platform ? { platform } : {},
      timeout: OWNER_APP_CONFIG_TIMEOUT_MS
    });
    return response.data;
  },

  // Public health check
  healthCheck: async () => {
    try {
      const response = await publicApi.get('/api/health');
      return response.data;
    } catch (error) {
      console.error('Health check error:', describeError(error));
      throw error;
    }
  }
};

export { publicApi, publicApiService };
export default publicApiService;